<script lang="ts">
	import ArrowUp from 'assets/svg/arrowUp.svg?component';
	import ArrowDown from 'assets/svg/arrowDown.svg?component';
	import { onDestroy, onMount, setContext } from 'svelte';
	import { browser } from '$app/environment';
	import theme from 'stores/theme';
	import activeCurrency from 'stores/activeCurrency';
	import activeWatchlist from 'stores/activeWatchlist';
	import WatchlistFilterTab from 'components/WatchlistFilterTab/WatchlistFilterTab.svelte';
	import type { FilterIntervalType } from 'customTypes/filterTypes';
	import WatchlistTable from 'components/WatchlistTable/WatchlistTable.svelte';
	import EmptyWatchlistBlock from 'components/EmptyWatchlistBlock/EmptyWatchlistBlock.svelte';
	import type { ITableData } from 'components/WatchlistTable/types';
	import AddWatchlistModal from 'components/AddWatchlistModal/AddWatchlistModal.svelte';
	import { _ } from 'utils/i18/i18n';
	import userStore from 'stores/user';
	import token from 'stores/token';
	import { editWatchlist } from 'services/watchlist/edit-watchlist';
	import { getWatchlist } from 'services/watchlist/get-watchlist';
	import { watchlistCoinList } from 'services/watchlist/watchlist-coin-list';
	import { getChartPreview } from 'services/coin-data/chart-preview';
	import MetaTags from 'components/MetaTags/MetaTags.svelte';
	import Validation from 'components/Validation/Validation.svelte';

	$: dark = $theme;
	let tableData: ITableData[] = [];
	let watchlist = [];
	let userLists = [];
	let isWatchlistMenuOpened = false;
	let isEditModeEnabled = false;
	let listMenuRef;
	let filterInterval: FilterIntervalType = '1d';
	let editListName = '';
	let addWatchlistModalIsOpened = false;
	let isLoading = false;
	let sparklines;
	let validationError = '';

	onMount(() => {
		if (browser) {
			document.addEventListener('click', handleClose);
		}
	});
	onDestroy(() => {
		if (browser) {
			document.removeEventListener('click', handleClose);
		}
	});

	const handleClose = (e: Event) => {
		if (listMenuRef && !listMenuRef.contains(e.target)) {
			isWatchlistMenuOpened = false;
		}
	};

	const toggleWatchlist = (list: string) => {
		userLists = Object.keys($userStore.watchlist);
		activeWatchlist.set({ name: list });
	};

	const setMainListIfDataEmpty = () => {
		activeWatchlist.set({ name: 'Main watchlist' });
		return $userStore?.watchlist['Main watchlist'];
	};

	const fetchData = async (isDeleted = false) => {
		if (!browser) return;

		isLoading = true;
		if ($userStore?._id.length > 0) {
			watchlist = (await $userStore?.watchlist[$activeWatchlist.name]) ?? setMainListIfDataEmpty();

			if (watchlist?.length > 0) {
				const data = await watchlistCoinList(
					watchlist?.join(),
					filterInterval,
					$activeCurrency.currency.toUpperCase(),
				);
				tableData = data;
			} else tableData = [];

			let symbols = tableData?.map((r) => r.symbol);

			sparklines = await getChartPreview(
				symbols,
				filterInterval,
				false,
				$activeCurrency.currency.toUpperCase(),
			);
			editListName = $activeWatchlist.name;

			if (isDeleted) {
				toggleWatchlist($_('watchlist.mainWatchList'));
			}
			userLists = Object.keys($userStore.watchlist);
			isLoading = false;
		}
	};

	const handleEdit = async () => {
		if (editListName.length > 0 && !!editListName.trim()) {
			isEditModeEnabled = false;
			const response = await editWatchlist(
				$userStore._id,
				$activeWatchlist.name,
				editListName,
				$token,
			);
			userStore.set({
				isLoggedIn: true,
				...response,
			});
			await toggleWatchlist(editListName);
			await fetchData();
		} else {
			validationError = 'Watchlist name should include only letters and numbers and not be empty';
		}
		await fetchData();
	};

	const handleClick = async (list) => {
		isWatchlistMenuOpened = !isWatchlistMenuOpened;
		activeWatchlist.set({ name: list });
		watchlist = await getWatchlist($userStore._id, list);
		await fetchData();
	};

	$: filterInterval || isEditModeEnabled || $activeWatchlist.name, fetchData(false);

	setContext('fetchData', fetchData);

	$: $userStore._id || $userStore.watchlist || $activeCurrency.currency, fetchData();

	onMount(fetchData);
</script>

<MetaTags
	title={'Watchlist | CryptoMarketCap'}
	description={'CryptoMarketCap\'s Watchlist'}
	noIndex={true}
/>

<div class="wrapper" class:dark>
	<div class="change-watchlist">
		<div class="change-header" bind:this={listMenuRef}>
			{#if !isEditModeEnabled}
				<!-- svelte-ignore a11y-click-events-have-key-events -->
				<!-- svelte-ignore a11y-no-static-element-interactions -->
				<div
					class="title-wrapper"
					on:click={() => {
						isWatchlistMenuOpened = !isWatchlistMenuOpened;
					}}
				>
					<h1 class="title no-select">{$activeWatchlist.name}</h1>
					{#if isWatchlistMenuOpened}
						<ArrowUp class="menu-arrow" width="8" height="5" />
					{:else}
						<ArrowDown class="menu-arrow" width="8" height="5" />
					{/if}
				</div>
			{:else}
				{#key watchlist}
					<div class="change-name">
						{#if validationError?.length > 0}
							<Validation message={validationError} />
						{/if}
						<input
							value={editListName}
							class="title-editing no-select"
							on:change={(e) => {
								editListName = e.target.value
									.replaceAll(/[^a-zA-Z0-9 \u2800]/g, '')
									.replaceAll(/\s\s+/g, ' ');
							}}
							maxlength={50}
						/>
						<button class="change-name-btn" on:click={handleEdit}>Save</button>
						<button
							class="change-name-btn"
							on:click={async () => {
								await fetchData();
								isEditModeEnabled = false;
							}}>Cancel</button
						>
					</div>
				{/key}
			{/if}

			<div class="list-wrapper" class:isOpened={isWatchlistMenuOpened}>
				<ul class="user-list">
					{#each userLists as list}
						<!-- svelte-ignore a11y-click-events-have-key-events -->
						<!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
						<li class="list-item" on:click={() => handleClick(list)}>
							{list}
						</li>
					{/each}
				</ul>
				<button
					type="button"
					class="new-list-btn"
					on:click={() => {
						addWatchlistModalIsOpened = true;
					}}>+ {$_('watchlist.newWatchlist')}</button
				>
			</div>
		</div>
	</div>

	{#key filterInterval || watchlist}
		<WatchlistFilterTab
			{dark}
			list={watchlist}
			listName={$activeWatchlist.name}
			bind:filterInterval
			bind:isEditModeEnabled
			{toggleWatchlist}
			watchlists={userLists}
		/>
		<WatchlistTable
			{dark}
			{tableData}
			{filterInterval}
			bind:sparklines
			bind:list={watchlist}
			listName={$activeWatchlist.name}
		/>
	{/key}
	{#if tableData?.length === 0 && !isLoading}
		<EmptyWatchlistBlock
			{dark}
			uid={$userStore._id}
			bind:list={watchlist}
			listName={$activeWatchlist.name}
		/>
	{/if}
	<AddWatchlistModal
		{dark}
		uid={$userStore._id}
		active={addWatchlistModalIsOpened}
		toggleModal={() => (addWatchlistModalIsOpened = !addWatchlistModalIsOpened)}
		{toggleWatchlist}
		watchlists={userLists}
	/>
</div>

<style lang="scss">
	@import 'watchlist';
</style>
